import React, { useState, useEffect } from 'react';
import { db, auth, storage } from '../../../firebase';
import { collection, addDoc, getDocs, query } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from '../../../context/AuthContext';


// Lista de departamentos
const departamentos = [
  'Ingeniería',
  'Psicología',
  'Medicina',
  'Matemáticas',
  'Física',
  'Biología',
  'Química',
  'Historia',
  'Literatura',
  'Otros'
];

// Lista de categorías
const categorias = [
  'Libros',
  'Artículos',
  'Tesis',
  'Notas',
  'Otros'
];

const Biblioteca = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const isPending = currentUser?.status === 'pending';

  const [user, setUser] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [filtroBusqueda, setFiltroBusqueda] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroDepartamento, setFiltroDepartamento] = useState('');

  // Estados para subida
  const [titulo, setTitulo] = useState('');
  const [categoria, setCategoria] = useState('');
  const [departamento, setDepartamento] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);

  // Estado para el modo (initial, upload, search)
  const [mode, setMode] = useState('initial');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchArchivos();
      setMode('initial');
    }
  }, [isOpen]);

  const fetchArchivos = async () => {
    try {
      const q = query(collection(db, 'biblioteca'));
      const querySnapshot = await getDocs(q);
      const archivosData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setArchivos(archivosData);
    } catch (err) {
      console.error('Error al cargar archivos:', err);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 3) {
      setError('Máximo 3 archivos permitidos.');
      return;
    }
    for (let file of selectedFiles) {
      if (file.size > 50 * 1024 * 1024) {
        setError('Cada archivo debe ser menor a 50MB.');
        return;
      }
    }
    setFiles(selectedFiles);
    setError('');
  };

  const uploadToLibrary = async (file) => {
  if (!file) throw new Error("No se seleccionó ningún archivo.");

  try {
    // Crear ruta única en Firebase Storage
    const filePath = `biblioteca/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);

    // Subir archivo
    const snapshot = await uploadBytes(storageRef, file);

    // Obtener URL pública
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error("Error al subir archivo a la biblioteca:", error);
    throw new Error("Error al subir archivo a Firebase Storage");
  }
};

  const handleSubir = async () => {
    if (!user) {
      setError('Debes estar autenticado para subir archivos.');
      return;
    }
    if (!titulo || !categoria || !departamento || files.length === 0) {
      setError('Completa todos los campos y selecciona al menos un archivo.');
      return;
    }
    setLoading(true);
    try {
      for (let file of files) {
        const url = await uploadToLibrary(file);
        await addDoc(collection(db, 'biblioteca'), {
          title: titulo,
          category: categoria,
          department: departamento,
          url,
          userId: user.uid,
          userName: user.displayName || user.email,
          uploadDate: new Date().toISOString(),
          fileName: file.name,
          downloads: 0
        });
      }
      setTitulo('');
      setCategoria('');
      setDepartamento('');
      setFiles([]);
      fetchArchivos();
      setError('');
      setSuccessMsg('✅ ¡Material subido exitosamente! Ya está disponible en la biblioteca.');
      setMode('initial');
    } catch (err) {
      setError('Error al subir: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const archivosFiltrados = archivos.filter(archivo => {
    const matchTitulo = archivo.title.toLowerCase().includes(filtroBusqueda.toLowerCase());
    const matchCategoria = !filtroCategoria || archivo.category === filtroCategoria;
    const matchDepartamento = !filtroDepartamento || archivo.department === filtroDepartamento;
    return matchTitulo && matchCategoria && matchDepartamento;
  });

  const handleDescargar = async (archivo) => {
    window.open(archivo.url, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] overflow-y-auto transform transition-all duration-300 ease-in-out scale-100 opacity-100 p-0">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Biblioteca SIT
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition duration-200 text-xl font-bold"
          >
            ✕
          </button>
        </div>
        <div className="p-6">
          {mode === 'initial' && (
            <div className="flex flex-col sm:flex-row justify-center items-center gap-6 animate-fade-in">
              {successMsg && (
                <div className="w-full sm:col-span-full bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 rounded-lg px-4 py-3 text-sm font-medium text-center mb-2">
                  {successMsg}
                </div>
              )}

              {/* Aviso cuenta pendiente */}
              {isPending && (
                <div className="w-full bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg px-4 py-3 flex items-start gap-3">
                  <span className="text-yellow-500 text-lg flex-shrink-0 mt-0.5">⏳</span>
                  <p className="text-yellow-700 dark:text-yellow-300 text-sm leading-relaxed">
                    Tu cuenta está <strong>pendiente de verificación</strong>. Podrás subir y buscar recursos una vez que el equipo de SIT la apruebe (1–6 horas).
                  </p>
                </div>
              )}

              <button
                onClick={() => !isPending && setMode('upload')}
                disabled={isPending}
                title={isPending ? 'Tu cuenta está pendiente de verificación' : ''}
                className={`w-full sm:w-auto py-4 px-8 rounded-lg text-lg font-semibold transition-all duration-300 shadow-lg
                  ${isPending
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-60'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transform hover:scale-105'
                  }`}
              >
                Subir Archivos
              </button>
              <button
                onClick={() => !isPending && setMode('search')}
                disabled={isPending}
                title={isPending ? 'Tu cuenta está pendiente de verificación' : ''}
                className={`w-full sm:w-auto py-4 px-8 rounded-lg text-lg font-semibold transition-all duration-300 shadow-lg
                  ${isPending
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-60'
                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:shadow-xl hover:from-green-600 hover:to-green-700 transform hover:scale-105'
                  }`}
              >
                Buscar Recursos
              </button>
              <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg shadow mb-4 text-center sm:text-left">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                    Bienvenido a la Biblioteca SIT
                </h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
                    Este es el espacio donde puedes compartir libros, notas, proyectos y todo tipo de documentos útiles para la comunidad. 
                    Explora, descarga y contribuye para que juntos construyamos un repositorio de recursos académico-valioso y accesible para todos.
                </p>
                </div>
            </div>
            
          )}

          {mode === 'upload' && (
            <div className="animate-fade-in">

              {/* Advertencia */}
              <div className="w-full bg-yellow-100 dark:bg-yellow-800 border-l-4 border-yellow-500 p-4 rounded-lg shadow text-gray-800 dark:text-gray-100 mb-6">
                <h3 className="text-lg font-bold mb-2 text-yellow-700 dark:text-yellow-300">
                  ⚠️ Advertencia de Subida de Documentos
                </h3>
                <p className="text-sm sm:text-base leading-relaxed">
                  Al subir cualquier documento, usted confirma que su contenido es de interés académico o universitario y contribuye a la comunidad. 
                  Tenga en cuenta que cualquier material que viole las normas éticas, los derechos de autor o no cumpla con este propósito será eliminado sin previo aviso. 
                  El incumplimiento de estas normas puede resultar en sanciones para el usuario. 
                  Es su responsabilidad asegurarse de que el material que comparte cumple con estas directrices.
                </p>
              </div>

              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
                Subir Archivos
              </h3>
              <input
                type="text"
                placeholder="Título del libro/recurso"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                className="w-full mb-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition duration-200"
              />
              <select
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                className="w-full mb-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition duration-200"
              >
                <option value="">Selecciona Categoría</option>
                {categorias.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <select
                value={departamento}
                onChange={(e) => setDepartamento(e.target.value)}
                className="w-full mb-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition duration-200"
              >
                <option value="">Selecciona Departamento</option>
                {departamentos.map(dep => (
                  <option key={dep} value={dep}>{dep}</option>
                ))}
              </select>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                className="w-full mb-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-gray-600 dark:file:text-gray-100 dark:hover:file:bg-gray-500 transition duration-200"
              />
              {error && <p className="text-red-500 mb-3 font-medium">{error}</p>}
              <button
                onClick={handleSubir}
                disabled={loading || !user}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg shadow-md hover:shadow-lg hover:from-blue-600 hover:to-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 font-semibold"
              >
                {loading ? 'Subiendo...' : 'Subir'}
              </button>
              <button
                onClick={() => setMode('initial')}
                className="mt-4 text-blue-500 dark:text-blue-400 hover:underline transition duration-200"
              >
                Volver al inicio
              </button>
            </div>
          )}


          {mode === 'search' && (
            <div className="animate-fade-in">
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Buscar Recursos</h3>
              <input
                type="text"
                placeholder="Buscar por título"
                value={filtroBusqueda}
                onChange={(e) => setFiltroBusqueda(e.target.value)}
                className="w-full mb-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 transition duration-200"
              />
              <select
                value={filtroCategoria}
                onChange={(e) => setFiltroCategoria(e.target.value)}
                className="w-full mb-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 transition duration-200"
              >
                <option value="">Filtrar por Categoría</option>
                {categorias.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <select
                value={filtroDepartamento}
                onChange={(e) => setFiltroDepartamento(e.target.value)}
                className="w-full mb-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 transition duration-200"
              >
                <option value="">Filtrar por Departamento</option>
                {departamentos.map(dep => (
                  <option key={dep} value={dep}>{dep}</option>
                ))}
              </select>

              <ul className="mt-6 space-y-4 max-h-60 overflow-y-auto">
                {archivosFiltrados.map(archivo => (
                  <li
                    key={archivo.id}
                    className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm hover:shadow-md transition duration-200"
                  >
                    <p className="text-gray-900 dark:text-gray-100 font-semibold">{archivo.title}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400"><strong>Categoría:</strong> {archivo.category}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400"><strong>Departamento:</strong> {archivo.department}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400"><strong>Subido por:</strong> {archivo.userName} el {new Date(archivo.uploadDate).toLocaleDateString()}</p>
                    <button
                      onClick={() => handleDescargar(archivo)}
                      className="mt-2 bg-gradient-to-r from-green-500 to-green-600 text-white py-2 px-4 rounded-lg shadow-md hover:shadow-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 text-sm font-semibold"
                    >
                      Descargar
                    </button>
                  </li>
                ))}
                {archivosFiltrados.length === 0 && <p className="text-gray-600 dark:text-gray-400 text-center">No se encontraron recursos.</p>}
              </ul>
              <button
                onClick={() => setMode('initial')}
                className="mt-4 text-blue-500 dark:text-blue-400 hover:underline transition duration-200"
              >
                Volver al inicio
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Biblioteca;